import ReactDOM from "react-dom/client";
import { SnackbarProvider } from "notistack";
import { Provider } from "react-redux";
import { StyledEngineProvider } from "@mui/material";
import { I18nextProvider, initReactI18next } from "react-i18next";
import i18next from "i18next";
import reportWebVitals from "./reportWebVitals";
import MainRouter from "./routes/MainRouter";
import { store } from "./store";
import Theme from "./Theme";
import EN from "./assets/translations/en/en.json";
import ES from "./assets/translations/es/es.json";
import Keys from "./constants/helper";
import CustomSnackBar from "./components/Notification/Notification";
import "./styles/index.css";
import "./styles/fonts.css";

type Language = "es" | "en";

const userData = localStorage.getItem(Keys.EPIC_USER_DATA);
const parsedData = userData ? JSON.parse(userData) : {};
const language: Language = parsedData.language || "es";

i18next.use(initReactI18next).init({
  interpolation: { escapeValue: false },
  lng: language, // language to use
  resources: {
    en: { translations: EN },
    es: { translations: ES },
  },
  ns: ["translations"], // define the namespace
  defaultNS: "translations",
});

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement,
);
root.render(
  <Provider store={store}>
    <I18nextProvider i18n={i18next}>
      <Theme>
        <StyledEngineProvider injectFirst>
          <SnackbarProvider
            Components={{
              error: CustomSnackBar,
              success: CustomSnackBar,
            }}
            autoHideDuration={5000}
          >
            <MainRouter />
          </SnackbarProvider>
        </StyledEngineProvider>
      </Theme>
    </I18nextProvider>
  </Provider>,
);

reportWebVitals();

// Service Worker — HLS manifest rewriter for iOS native fullscreen audio
// selection. See public/sw-hls.js and docs/hls/sw-rewrite.md for the why.
// Forwards SW_HLS_* postMessages to the regular console so STG diagnostics
// don't require opening the dedicated SW DevTools surface.
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    console.log("[SW-HLS bootstrap] attempting register", {
      origin: window.location.origin,
      controllerBefore: !!navigator.serviceWorker.controller,
      controllerUrl: navigator.serviceWorker.controller?.scriptURL ?? null,
    });
    navigator.serviceWorker
      .register("/sw-hls.js", { scope: "/" })
      .then((reg) => {
        console.log("[SW-HLS bootstrap] register OK", {
          scope: reg.scope,
          active: !!reg.active,
          installing: !!reg.installing,
          waiting: !!reg.waiting,
        });
      })
      .catch((err) => {
        console.error("[SW-HLS bootstrap] register FAILED", err);
      });

    navigator.serviceWorker.addEventListener("message", (event) => {
      const data = event.data;
      if (
        data &&
        typeof data === "object" &&
        typeof data.type === "string" &&
        data.type.startsWith("SW_HLS_")
      ) {
        console.log("[SW-HLS msg]", data.type, data);
      }
    });

    navigator.serviceWorker.addEventListener("controllerchange", () => {
      console.log("[SW-HLS bootstrap] controllerchange", {
        controllerUrl: navigator.serviceWorker.controller?.scriptURL ?? null,
      });
    });
  });
}
